import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, AuthContext } from '../App';
import { Users, UserPlus, Check, Clock, ChevronRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

/**
 * Players You May Know - Second-degree connection suggestions
 */
const PlayerSuggestions = ({ visible = true, maxItems } = {}) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const identity = useRef({ id: user?.id });
  if (identity.current.id !== user?.id) identity.current = { id: user?.id };
  const [suggestions, setSuggestions] = useState({ owner: null, items: [] });
  const [loading, setLoading] = useState(true);
  const [followStates, setFollowStates] = useState({});
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    if (!user?.id) return;
    let active = true;
    const owner = identity.current;
    const current = () => active && identity.current === owner;
    const fetchSuggestions = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/users/suggestions?limit=6');
      if (!current()) return;
      setSuggestions({ owner, items: res.data.suggestions || [] });
      
      // Initialize follow states as 'none'
      const initialStates = {};
      (res.data.suggestions || []).forEach(s => {
        initialStates[s.id] = 'none';
      });
      setFollowStates(initialStates);
      setActionLoading({});
    } catch (error) {
      if (current()) console.error('Failed to fetch suggestions:', error);
    } finally {
      if (current()) setLoading(false);
    }
    };
    fetchSuggestions();
    return () => { active = false; };
  }, [user?.id]);

  const handleFollow = async (userId, username) => {
    const owner = identity.current;
    setActionLoading(prev => ({ ...prev, [userId]: true }));
    try {
      const response = await apiClient.post(`/follow/${userId}`);
      if (identity.current !== owner) return;
      const data = response.data;
      
      if (data.following) {
        setFollowStates(prev => ({ ...prev, [userId]: 'following' }));
        toast.success(`Following ${username}`);
      } else if (data.request_pending) {
        setFollowStates(prev => ({ ...prev, [userId]: 'requested' }));
        toast.success(`Follow request sent to ${username}`);
      }
    } catch (error) {
      if (identity.current === owner) toast.error('Failed to follow');
    } finally {
      if (identity.current === owner) setActionLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Don't render if no suggestions or loading
  if (!visible || loading || suggestions.owner !== identity.current) {
    return null; // Silent loading, don't show skeleton
  }

  if (suggestions.items.length === 0) {
    return null; // Don't show section if no suggestions
  }

  return (
    <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden" data-testid="player-suggestions">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-orange-500" />
          <h3 className="text-sm font-semibold text-zinc-900">Players You May Know</h3>
        </div>
      </div>

      {/* Suggestions List */}
      <div className="divide-y divide-zinc-50">
        {suggestions.items.slice(0, maxItems).map(suggestion => (
          <div 
            key={suggestion.id}
            className="px-4 py-3 flex items-center gap-3"
          >
            {/* Avatar */}
            <button
              onClick={() => navigate(`/player/${suggestion.username}`)}
              className="flex-shrink-0"
            >
              {suggestion.profile_image_url ? (
                <img
                  src={suggestion.profile_image_url.startsWith('http') 
                    ? suggestion.profile_image_url 
                    : `${process.env.REACT_APP_BACKEND_URL || ''}${suggestion.profile_image_url}`}
                  alt={suggestion.username}
                  className="w-10 h-10 rounded-full object-cover ring-2 ring-zinc-100"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-zinc-200 to-zinc-300 flex items-center justify-center ring-2 ring-zinc-100">
                  <span className="text-sm font-semibold text-zinc-500">
                    {suggestion.username?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
              )}
            </button>

            {/* Info */}
            <button
              onClick={() => navigate(`/player/${suggestion.username}`)}
              className="flex-1 min-w-0 text-left"
            >
              <p className="text-sm font-medium text-zinc-900 truncate">{suggestion.display_name || suggestion.username}</p>
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                {suggestion.skill_level && suggestion.skill_level !== 'Unranked' && (
                  <span>{suggestion.skill_level}</span>
                )}
                {suggestion.age_group && (
                  <span>{suggestion.age_group}</span>
                )}
                {suggestion.mutual_count > 0 && (
                  <span className="text-orange-500">
                    {suggestion.mutual_count} mutual
                  </span>
                )}
              </div>
            </button>

            {/* Follow Button */}
            {followStates[suggestion.id] === 'following' ? (
              <Button
                variant="outline"
                size="sm"
                disabled
                className="h-8 px-3 text-xs rounded-full text-zinc-600 border-zinc-300"
              >
                <Check className="w-3 h-3 mr-1" />
                Following
              </Button>
            ) : followStates[suggestion.id] === 'requested' ? (
              <Button
                variant="outline"
                size="sm"
                disabled
                className="h-8 px-3 text-xs rounded-full text-orange-600 border-orange-300"
              >
                <Clock className="w-3 h-3 mr-1" />
                Requested
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => handleFollow(suggestion.id, suggestion.username)}
                disabled={actionLoading[suggestion.id]}
                className="h-8 px-3 text-xs rounded-full bg-orange-500 hover:bg-orange-600 text-white"
              >
                {actionLoading[suggestion.id] ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <UserPlus className="w-3 h-3 mr-1" />
                    Follow
                  </>
                )}
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlayerSuggestions;
